import request, { FAIL, SUCCESS } from '../util/Request';
import { fileKeyToDownloadType } from '../util/FileHelpers';

type UpdateList = [number, string, string?, string?, string?];

interface DownloadResponse {
  status: string;
  [key: string]: unknown;
}

type GetAssignVocResponseType = {
  status: string;
  data?: unknown;
};

type VocListOptions = {
  page: number;
  size: number;
  adminPassword: string;
  progress: string;
  nameKeyword?: string;
  titleKeyword?: string;
  empNmKeyword?: string;
  serviceType?: string;
  companyNameKeyword?: string;
  startDate?: string;
  endDate?: string;
};

// SERVICE 탭 리스트 조회 옵션 타입
type ServiceListOptions = {
  page: number;
  size: number;
  requestType?: string; // 서비스종류: messaging, aicc
  corpType?: string; // 법인종류: general, special, reseller
  corpName?: string; // 법인명
  bizRegNo?: string; // 사업자등록번호
  title?: string; // 제목
  contactName?: string; // 담당자명
  status?: string; // 승인상태: success, pending, fail, cancel
  startDate?: string; // 시작일자
  endDate?: string; // 종료일자
};

const AdminBoardService = {
  // ========== SERVICE 탭 API ==========
  // 서비스 신청 목록 조회 (검색)
  getServiceBoardList: async (opts: ServiceListOptions) => {
    const {
      page,
      size,
      requestType,
      corpType,
      corpName,
      bizRegNo,
      title,
      contactName,
      status,
      startDate,
      endDate
    } = opts;

    try {
      // 모든 파라미터를 항상 전송 (값이 없거나 'all'이면 빈 스트링)
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('size', String(size));
      params.append('requestType', requestType === 'all' || !requestType ? '' : requestType);
      params.append('corpType', corpType === 'all' || !corpType ? '' : corpType);
      params.append('corpName', corpName || '');
      params.append('bizRegNo', bizRegNo || '');
      params.append('title', title || '');
      params.append('contactName', contactName || '');
      params.append('status', status === 'all' || !status ? '' : status);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const url = `/serviceRequests/boards?${params.toString()}&sort=createdAt,desc`;
      const response = await request.get(url);

      if (request.isSuccess(response)) {
        return {
          ...response,
          status: SUCCESS
        };
      }
      return { status: FAIL };
    } catch (error) {
      return { status: FAIL };
    }
  },

  // 서비스 신청 전체 목록 조회
  getServiceAllBoardList: async (page: number, size: number) => {
    try {
      const url = `/serviceRequests/allboards?page=${page}&size=${size}`;
      const response = await request.get(url);

      if (request.isSuccess(response)) {
        return {
          ...response,
          status: SUCCESS
        };
      }
      return { status: FAIL };
    } catch (error) {
      return { status: FAIL };
    }
  },

  // 서비스 신청 단일 조회
  getServiceBoard: async (requestId: number) => {
    try {
      const url = `/serviceRequests/board?requestId=${requestId}`;
      const response = await request.get(url);

      if (request.isSuccess(response)) {
        return {
          ...response,
          status: SUCCESS
        };
      }
      return { status: FAIL };
    } catch (error) {
      return { status: FAIL };
    }
  },

  // 서비스 신청 상세 조회 (requestId를 쿼리로)
  getServiceRequestDetail: async (requestId: number) => {
    try {
      const url = `/serviceRequests/requestDetail?requestId=${requestId}`;
      const response = await request.get(url);

      if (request.isSuccess(response)) {
        return {
          ...response,
          status: SUCCESS
        };
      }
      return { status: FAIL };
    } catch (error) {
      return { status: FAIL };
    }
  },

  // ========== VOC 탭 API ==========
  getVocBoardList: async (opts: VocListOptions) => {
    const {
      page,
      size,
      adminPassword,
      progress,
      nameKeyword,
      titleKeyword,
      empNmKeyword,
      serviceType,
      companyNameKeyword,
      startDate,
      endDate
    } = opts;

    try {
      let baseUrl = `/vocAdmin?page=${page}&size=${size}&pw=${adminPassword}&accept=${progress}&nameKeyword=${nameKeyword || ''}&titleKeyword=${titleKeyword || ''}&empNmKeyword=${empNmKeyword || ''}`;
      if (
        typeof companyNameKeyword !== 'undefined' &&
        companyNameKeyword !== null &&
        String(companyNameKeyword) !== ''
      ) {
        baseUrl += `&companyName=${encodeURIComponent(String(companyNameKeyword))}`;
      }
      if (startDate) {
        baseUrl += `&startDate=${encodeURIComponent(startDate)}`;
      }
      if (endDate) {
        baseUrl += `&endDate=${encodeURIComponent(endDate)}`;
      }
      const url =
        typeof serviceType !== 'undefined'
          ? `${baseUrl}&serviceType=${encodeURIComponent(String(serviceType))}`
          : baseUrl;
      const response = await request.get(url);
      if (request.isSuccess(response)) {
        return {
          ...response,
          status: SUCCESS
        };
      }
      return { status: FAIL };
    } catch (error) {
      return {
        status: FAIL
      };
    }
  },

  getAssignVoc: async (): Promise<GetAssignVocResponseType> => {
    try {
      const response = await request.get(`/empList`);

      if (request.isSuccess(response)) {
        return {
          ...response,
          status: SUCCESS
        };
      }
      return { status: FAIL };
    } catch (error) {
      return {
        status: FAIL
      };
    }
  },

  getSearchRangeDate: async (formatDate: string[]) => {
    try {
      const response = await request.get(
        `/dateCheck?searchStartDate=${formatDate[0]}&searchEndDate=${formatDate[1]}`
      );

      if (request.isSuccess(response)) {
        return {
          ...response,
          status: SUCCESS
        };
      }
      return { status: FAIL };
    } catch (error) {
      return {
        status: FAIL
      };
    }
  },

  updateAdminVoc: async (...props: UpdateList) => {
    const number = props[0];
    const accept = props[1];
    const selectAssignPerson = props[2];
    const selectAssignPersonName = props[3];
    try {
      const response = await request.post(`/vocAdmin`, {
        no: number,
        accept: accept,
        empId: selectAssignPerson,
        empNm: selectAssignPersonName
      });
      if (request.isSuccess(response)) {
        return {
          ...response,
          status: SUCCESS
        };
      }
      return { status: FAIL };
    } catch (error) {
      return {
        status: FAIL
      };
    }
  },

  // 서비스 신청 수정 (PUT - requestType에 따라 endpoint 분기)
  updateServiceRequest: async (
    requestId: number,
    data: Record<string, unknown>,
    files: Record<string, File>,
    requestType: string
  ): Promise<{ status: string; data?: unknown }> => {
    try {
      const formData = new FormData();

      // requestId를 데이터에 추가
      const dataWithId = {
        ...data
      };

      // data를 JSON blob으로 추가
      const dataBlob = new Blob([JSON.stringify(dataWithId)], { type: 'application/json' });
      formData.append('data', dataBlob, 'blob');

      // 파일들 추가 (fileCode로 key 지정)
      Object.entries(files).forEach(([fileCode, file]) => {
        if (file) {
          formData.append(fileCode, file);
        }
      });

      // requestType에 따라 endpoint 분기
      const endpoint =
        requestType === 'aicc' ? '/serviceRequest/aicc' : '/serviceRequest/messaging';

      const response = await request.put(`${endpoint}?requestId=${requestId}`, formData, {});

      if (request.isSuccess(response)) {
        return {
          status: SUCCESS,
          data: response.data
        };
      }
      return { status: FAIL };
    } catch (error) {
      console.error('updateServiceRequest error:', error);
      return { status: FAIL };
    }
  },

  // 서비스 신청 승인/거절 상태 변경 (requestStatus)
  updateServiceStatus: async (
    requestId: number,
    status: string
  ): Promise<{
    status: string;
    data?: { requestId: number; status: string; createdAt: string };
  }> => {
    try {
      const response = await request.put(
        `/serviceRequests/status?requestId=${requestId}&status=${encodeURIComponent(status)}`
      );
      if (request.isSuccess(response)) {
        return {
          status: SUCCESS,
          data: response.data
        };
      }
      return { status: FAIL };
    } catch (error) {
      console.error('updateServiceStatus error:', error);
      return { status: FAIL };
    }
  },

  // 서비스신청 파일 다운로드 (requestId, fileId로 요청)
  serviceFileDownload: async (
    requestId: number,
    fileId: number,
    fileName?: string
  ): Promise<DownloadResponse> => {
    try {
      // 쿼리파람으로 fileId, requestId 전달
      const url = `/serviceRequests/file?requestId=${requestId}&fileId=${fileId}`;
      const response = await request
        .get(url, {
          responseType: 'blob'
        })
        .then(response => {
          const contentType =
            (response.headers && response.headers['content-type']) || 'application/octet-stream';
          const blob = new Blob([response.data], { type: contentType });

          let downloadName = fileName || '다운로드';
          const disposition =
            response.headers &&
            (response.headers['content-disposition'] || response.headers['Content-Disposition']);
          if (disposition) {
            const fileNameMatch = disposition.match(/filename\*=UTF-8''([^;\n]+)/);
            if (fileNameMatch && fileNameMatch[1]) {
              downloadName = decodeURIComponent(fileNameMatch[1]);
            } else {
              const secondMatch = disposition.match(/filename="?([^;\n"]+)"?/);
              if (secondMatch && secondMatch[1]) {
                downloadName = secondMatch[1];
              }
            }
          }

          const fileObjectUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = fileObjectUrl;
          a.download = downloadName;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            window.URL.revokeObjectURL(fileObjectUrl);
          }, 60000);
          a.remove();
          return response;
        });

      if (request.isSuccess(response)) {
        return {
          ...response,
          status: SUCCESS
        };
      }
      return { status: FAIL };
    } catch (error) {
      console.error('serviceFileDownload error:', error);
      return { status: FAIL };
    }
  },

  // 파일 다운로드
  fileDownload: async (
    no: number | undefined,
    fileKey?: string,
    fileName?: string
  ): Promise<DownloadResponse> => {
    try {
      const typeParam = fileKeyToDownloadType(fileKey);
      const url = `/download?no=${no}${typeParam ? `&type=${encodeURIComponent(typeParam)}` : ''}`;
      const response = await request
        .get(url, {
          responseType: 'blob'
        })
        .then(response => {
          const contentType =
            (response.headers && response.headers['content-type']) || 'application/octet-stream';
          const blob = new Blob([response.data], { type: contentType });

          // Try to derive filename from Content-Disposition header if present
          let downloadName = fileName || '다운로드';
          const disposition =
            response.headers &&
            (response.headers['content-disposition'] || response.headers['Content-Disposition']);
          if (disposition) {
            const fileNameMatch = disposition.match(/filename\*=UTF-8''([^;\n]+)/);
            if (fileNameMatch && fileNameMatch[1]) {
              downloadName = decodeURIComponent(fileNameMatch[1]);
            } else {
              const secondMatch = disposition.match(/filename="?([^;\n"]+)"?/);
              if (secondMatch && secondMatch[1]) {
                downloadName = secondMatch[1];
              }
            }
          }

          const fileObjectUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = fileObjectUrl;
          a.download = downloadName;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            window.URL.revokeObjectURL(fileObjectUrl);
          }, 60000);
          a.remove();
          return response;
        });

      if (request.isSuccess(response)) {
        return {
          ...response,
          status: SUCCESS
        };
      }
      return { status: FAIL };
    } catch (error) {
      console.error('fileDownload error:', error);
      return {
        status: FAIL
      };
    }
  }
};

export default AdminBoardService;
